import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Beneficiary } from 'src/app/interfaces/beneficiary.interface';
import { BeneficiaryService } from 'src/app/services/beneficiary.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

/** Alias is optional; when provided, must be at least 4 characters */
const aliasMinLengthValidator = (control: AbstractControl): ValidationErrors | null => {
  const v = (control?.value ?? '').trim();
  if (v.length === 0) return null;
  return v.length >= 4 ? null : { minlength: { requiredLength: 4 } };
};

/** Emails array must have at least one valid email entry */
const atLeastOneEmailValidator = (control: AbstractControl): ValidationErrors | null => {
  const arr = control?.value as any[];
  if (!arr || arr.length === 0) return { atLeastOneEmail: true };
  const hasValid = arr.some((e: any) => e?.email && e?.email_type?.name);
  return hasValid ? null : { atLeastOneEmail: true };
};

/** Phones array must have at least one valid phone entry */
const atLeastOnePhoneValidator = (control: AbstractControl): ValidationErrors | null => {
  const arr = control?.value as any[];
  if (!arr || arr.length === 0) return { atLeastOnePhone: true };
  const hasValid = arr.some((p: any) => p?.phone_number && p?.phone_type?.name);
  return hasValid ? null : { atLeastOnePhone: true };
};

@Component({
  selector: 'app-pdl-person-modal',
  templateUrl: './pdl-person-modal.component.html',
  styleUrls: ['./pdl-person-modal.component.css']
})
export class PdlPersonModalComponent {
  selectedFiles?: FileList;
  actualFileSize?: number = 0;
  validPdf: boolean = false;
  pdfContent: Uint8Array = new Uint8Array(0);
  convertedActualFileSize?: string = '';


  @Input() sociedadRfc: string;
  @Input() sociedadNombre: string;
  @Input() shareholderUsuarioRfc: string;
  @Input() shareholderNombre: string;
  @Input() beneficiary: Beneficiary | null = null;

  personForm: FormGroup;
  /** Files for each identity item; kept in sync with identitys FormArray */
  identityFiles: (File | null)[] = [];
  /** Validation error per identity index */
  identityFileErrors: { [index: number]: string } = {};
  /** Last save error - shown in modal so user can correct or screenshot */
  lastSaveError: string | null = null;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private beneficiaryService: BeneficiaryService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {
    this.personForm = this.fb.group({
      full_name: ['', [Validators.required, Validators.minLength(4)]],
      alias: ['', [aliasMinLengthValidator]],
      born_date: [''],
      born_city: [''],
      born_country: [''],
      gender: [''],
      marital_status: [''],

      emails: this.fb.array([], [atLeastOneEmailValidator]),
      phones: this.fb.array([], [atLeastOnePhoneValidator]),
      relatives: this.fb.array([]),
      identitys: this.fb.array([])
    });
  }

  // ========== Emails ==========
  get emails(): FormArray {
    return this.personForm.get('emails') as FormArray;
  }
  readonly EMAIL_TYPES = ['Trabajo', 'Personal', 'Oficina'];

  addEmail() {
    const emailGroup = this.fb.group({
      id: [''],
      email_type: this.fb.group({
        id: [''],
        name: ['', Validators.required]
      }),
      email: ['', [Validators.required, Validators.email]]
    });
    this.emails.push(emailGroup);
  }
  removeEmail(index: number) {
    this.emails.removeAt(index);
  }

  // ========== Phones ==========
  get phones(): FormArray {
    return this.personForm.get('phones') as FormArray;
  }
  readonly PHONE_TYPES = ['Celular', 'Casa', 'Oficina', 'otro'];

  addPhone() {
    const phoneGroup = this.fb.group({
      id: [''],
      phone_type: this.fb.group({
        id: [''],
        name: ['', Validators.required]
      }),
      phone_number: ['', [Validators.required, Validators.minLength(8)]]
    });
    this.phones.push(phoneGroup);
  }
  removePhone(index: number) {
    this.phones.removeAt(index);
  }

  // ========== Relatives ==========
  get relatives(): FormArray {
    return this.personForm.get('relatives') as FormArray;
  }
  readonly RELATIVE_TYPES = ['Hermano(a)', 'Esposa(o)', 'Hijo(a)', 'Otro'];

  addRelative() {
    const relativeGroup = this.fb.group({
      id: [''],
      relative_type: this.fb.group({
        id: [''],
        name: ['', Validators.required]
      }),
      name: ['', [Validators.required, Validators.minLength(4)]],
      id_type: this.fb.group({
        id: [''],
        name: ['']
      }),
      address: this.fb.group({
        id: [''],
        address_type: this.fb.group({
          id: [''],
          name: ['']
        }),
        name: [''],
        street: [''],
        house_number: [''],
        zip_code: [''],
        country: ['']
      })
    });
    this.relatives.push(relativeGroup);
  }
  removeRelative(index: number) {
    this.relatives.removeAt(index);
  }

  // ========== Identities ==========
  get identitys(): FormArray {
    return this.personForm.get('identitys') as FormArray;
  }
  addIdentity() {
    const identityGroup = this.fb.group({
      id: [''],
      identity_type: this.fb.group({
        id: [''],
        name: ['']
      }),
      name: [''],
      authority: [''],
      couuntry: [''], // note: typo in your class, maybe should be "country"
      document: [''],
      id_number: ['']
    });
    this.identitys.push(identityGroup);
    this.identityFiles.push(null);
    delete this.identityFileErrors[this.identitys.length - 1];
  }
  removeIdentity(index: number) {
    this.identitys.removeAt(index);
    this.identityFiles.splice(index, 1);
    const nextErrors: { [index: number]: string } = {};
    Object.keys(this.identityFileErrors).forEach(k => {
      const i = parseInt(k, 10);
      if (i < index) nextErrors[i] = this.identityFileErrors[i];
      else if (i > index) nextErrors[i - 1] = this.identityFileErrors[i];
    });
    this.identityFileErrors = nextErrors;
  }

  onIdentityFileSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    delete this.identityFileErrors[index];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      this.identityFileErrors[index] = 'Solo se permiten PDF, JPEG o PNG.';
      input.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.identityFileErrors[index] = 'El archivo no debe superar 2 MB.';
      input.value = '';
      return;
    }
    this.identityFiles[index] = file;
    this.identitys.at(index).get('document')?.setValue(file.name);
  }

  clearIdentityFile(index: number): void {
    this.identityFiles[index] = null;
    this.identitys.at(index).get('document')?.setValue('');
    delete this.identityFileErrors[index];
    const input = document.getElementById(`identity-file-${index}`) as HTMLInputElement;
    if (input) input.value = '';
  }

  getIdentityFileName(index: number): string {
    const file = this.identityFiles[index];
    return file ? file.name : '';
  }

  // ========== Save ==========
  save() {
    if (!this.personForm.valid) {
      this.personForm.markAllAsTouched();
      this.toastr.warning('Por favor complete todos los campos requeridos.');
      return;
    }
    if (!this.sociedadRfc || !this.shareholderUsuarioRfc) {
      this.toastr.error('Falta el contexto de sociedad o accionista. Cierre y vuelva a abrir desde la lista de accionistas.');
      return;
    }

    this.lastSaveError = null;
    this.spinner.show();
    this.buildBeneficiaryPayload().then((payload) => {
      if (this.beneficiary?.id) {
        payload.id = this.beneficiary.id;
        payload.shareholderId = this.beneficiary.shareholderId;
        payload.sociedadRfc = this.sociedadRfc;
        payload.shareholderUsuarioRfc = this.shareholderUsuarioRfc;
        console.log('Payload:');
        console.log(payload);
        this.beneficiaryService.update(payload).subscribe(
          (res: any) => this.handleSaveSuccess(res),
          (err) => this.handleSaveError(err)
        );
      } else {
        this.beneficiaryService.add(payload, this.sociedadRfc, this.shareholderUsuarioRfc).subscribe(
          (res: any) => this.handleSaveSuccess(res),
          (err) => this.handleSaveError(err)
        );
      }
    }).catch((err) => {
      this.spinner.hide();
      this.lastSaveError = err?.message || 'Error al procesar los archivos.';
      this.toastr.error(this.lastSaveError + ' Los datos se mantienen. Puede corregir y reintentar, o tomar una captura para soporte.');
    });
  }

  private async buildBeneficiaryPayload(): Promise<Beneficiary> {
    const form = this.personForm.value;
    const identitys = [];
    for (let i = 0; i < form.identitys.length; i++) {
      const iden = form.identitys[i];
      let documentBase64 = '';
      if (this.identityFiles[i]) {
        documentBase64 = await this.fileToBase64(this.identityFiles[i]!);
      }
      identitys.push({
        identityTypeName: iden.identity_type?.name || '',
        idNumber: iden.id_number || '',
        authority: iden.authority || '',
        country: iden.couuntry || iden.country || '',
        fileName: this.identityFiles[i]?.name || '',
        contentType: this.identityFiles[i]?.type || '',
        documentBase64: documentBase64 || undefined
      });
    }
    return {
      sociedadRfc: this.sociedadRfc,
      shareholderUsuarioRfc: this.shareholderUsuarioRfc,
      fullName: form.full_name,
      alias: form.alias || '',
      bornDate: form.born_date || undefined,
      bornCity: form.born_city || '',
      bornCountry: form.born_country || '',
      gender: form.gender || '',
      maritalStatus: form.marital_status || '',
      emails: (form.emails || []).map((e: any) => ({
        emailTypeName: e.email_type?.name || '',
        email: e.email
      })),
      phones: (form.phones || []).map((p: any) => ({
        phoneTypeName: p.phone_type?.name || '',
        phoneNumber: p.phone_number
      })),
      relatives: (form.relatives || []).map((r: any) => ({
        relativeTypeName: r.relative_type?.name || '',
        name: r.name,
        street: r.address?.street || '',
        houseNumber: r.address?.house_number || '',
        zipCode: r.address?.zip_code || '',
        country: r.address?.country || ''
      })),
      identitys
    };
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1] || result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private handleSaveSuccess(res: any): void {
    this.spinner.hide();
    if (res?.httpStatus === 200) {
      this.lastSaveError = null;
      this.toastr.success('Beneficiario guardado correctamente.');
      this.activeModal.close(res?.beneficiary || true);
    } else {
      const msg = res?.messageDetail || 'Error al guardar.';
      this.lastSaveError = msg;
      this.toastr.error(msg);
      // Modal stays open - user can correct data or take screenshot for support
    }
  }

  private handleSaveError(err: any): void {
    this.spinner.hide();
    const msg = err?.error?.messageDetail || err?.message || 'Error al guardar el beneficiario.';
    this.lastSaveError = msg;
    this.toastr.error(msg + ' Los datos se mantienen. Puede corregir y reintentar, o tomar una captura para soporte.');
    // Modal stays open - data is preserved for correction or screenshot
  }

  formatFileSize(sizeInBytes: number): string {
    const KB = 1024;
    const MB = KB * 1024;
    const GB = MB * 1024;
  
    if (sizeInBytes < KB) {
      return sizeInBytes + ' B';
    } else if (sizeInBytes < MB) {
      return (sizeInBytes / KB).toFixed(2) + ' KB';
    } else if (sizeInBytes < GB) {
      return (sizeInBytes / MB).toFixed(2) + ' MB';
    } else {
      return (sizeInBytes / GB).toFixed(2) + ' GB';
    }
  }

//  Angular 12
  loadPdfContent(file: File): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        // const arrayBuffer = event.target?.result as ArrayBuffer;
        // const arrayBuffer = event.target.result as ArrayBuffer;
        const arrayBuffer = event.target.result as ArrayBuffer;
        if (arrayBuffer) {
          const uint8Array = new Uint8Array(arrayBuffer);
          resolve(uint8Array);
        } else {
          reject(new Error('Failed to read file content.'));
        }
      };
  
      // Pass the File object directly to readAsArrayBuffer
      reader.readAsArrayBuffer(file);
    });
  }

  selectFile(event: any): void {
    this.selectedFiles = event.target.files;
    // this.actualFileSize = this.selectedFiles?.item(0)?.size;
    this.actualFileSize = this.selectedFiles.item(0).size;
    this.validPdf = false;
  
    if (this.selectedFiles && this.actualFileSize !== undefined) {
      const maxSizeInBytes = 20 * 1024 * 1024; // 20 MB in bytes
  
      if (this.actualFileSize <= maxSizeInBytes) {
        this.validPdf = true;
        const file = this.selectedFiles[0]; // Use the first file in the list
        // const file = event.target.files[0]; // Another way to get the first file
  
        this.loadPdfContent(file).then((pdfContent) => {
          this.pdfContent = pdfContent;
        }).catch((error) => {
          console.error('Error loading PDF content:', error);
        });
  
        console.info("File size ok" + this.formatFileSize(this.actualFileSize));
        this.convertedActualFileSize = this.formatFileSize(this.actualFileSize);
      } else {
        console.error('File size exceeds the allowed limit (20 MB).');
        this.pdfContent = new Uint8Array(0);
      }
    }
  }


}
